import { useNotificationModal } from '@/src/context/notificationModal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Notification, NotificationCounts, NotificationService } from '../../services/notificationService';
import { ANIMATION_DURATION, POLLING_INTERVAL } from '../NotificationModal/constants';
import { BellIcon } from '../NotificationModal/icons';
import { NotificationPopup } from '../NotificationPopup';
import { BadgeContainer, BadgeCount, BellIconWrapper } from './styles';

export function NotificationBadge() {
  const { openModal, isOpen } = useNotificationModal();
  const [counts, setCounts] = useState<NotificationCounts | null>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [newNotification, setNewNotification] = useState<Notification | null>(null);
  const previousCountRef = useRef<number>(0);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastNotificationIdRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadCounts = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const data = await NotificationService.getCounts();
      if (!isMountedRef.current) return;

      if (!isInitialLoadRef.current && !isOpen && previousCountRef.current > 0 && data.not_completed > previousCountRef.current) {
        setShouldAnimate(true);

        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }

        animationTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) setShouldAnimate(false);
        }, ANIMATION_DURATION);

        try {
          const notifications = await NotificationService.getNotifications({
            completed: false,
            limit: 1
          });
          if (!isMountedRef.current) return;

          if (notifications.length > 0) {
            const latestNotification = notifications[0];
            const notificationDate = new Date(latestNotification.created_at);
            const now = new Date();
            const diffSeconds = (now.getTime() - notificationDate.getTime()) / 1000;

            if (latestNotification.id !== lastNotificationIdRef.current && diffSeconds < 60) {
              lastNotificationIdRef.current = latestNotification.id;
              setNewNotification(latestNotification);
            }
          }
        } catch (err: any) {
          console.error('Erro ao buscar última notificação:', err);
        }
      }

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        if (data.not_completed > 0) {
          try {
            const notifications = await NotificationService.getNotifications({
              completed: false,
              limit: 1
            });
            if (isMountedRef.current && notifications.length > 0) {
              lastNotificationIdRef.current = notifications[0].id;
            }
          } catch (err: any) {
          }
        }
      }

      previousCountRef.current = data.not_completed;
      if (isMountedRef.current) setCounts(data);
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        console.error('Erro ao carregar contadores de notificações:', err);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    loadCounts();

    const interval = setInterval(loadCounts, POLLING_INTERVAL);

    return () => {
      clearInterval(interval);
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [loadCounts]);

  const handleClick = () => {
    openModal();
  };

  const handleClosePopup = useCallback(() => {
    setNewNotification(null);
  }, []);

  const handleViewAll = useCallback(() => {
    handleClosePopup();
    openModal();
  }, [handleClosePopup, openModal]);

  const unreadCount = counts?.not_completed || 0;
  const hasNotifications = unreadCount > 0;

  return (
    <>
      <BadgeContainer
        onClick={handleClick}
        title={hasNotifications ? `${unreadCount} notificações pendentes` : 'Notificações'}
        hasNotifications={hasNotifications}
        aria-label={hasNotifications ? `${unreadCount} notificações pendentes` : 'Abrir notificações'}
      >
        <BellIconWrapper>
          <BellIcon />
        </BellIconWrapper>
        {hasNotifications && (
          <BadgeCount animate={shouldAnimate} aria-live="polite">
            {unreadCount > 99 ? '99+' : unreadCount}
          </BadgeCount>
        )}
      </BadgeContainer>

      {newNotification && (
        <NotificationPopup
          notification={newNotification}
          onClose={handleClosePopup}
          onViewAll={handleViewAll}
        />
      )}
    </>
  );
}
